import { FC, ReactNode, useCallback, useMemo } from 'react';

import { ChatContext, ChatContextValue, ChatArgs } from '@eneco/components/chat/useChat';

import { useChatV2, Namespace, Visibility } from './useChatV2';

const visibilityMap: Record<string, Visibility> = {
  hidden: 'minimized',
  minimized: 'minimized',
  open: 'compact',
};

interface ContextBridgeProps {
  children: ReactNode;
  defaultNamespace?: Namespace;
}

/**
 * ContextBridge bridges the V1 useChat API to V2 useChatV2 API.
 * This allows existing components using useChat() to work seamlessly
 * with the new V2 chatbot implementation during the A/B test period.
 *
 * Token handling is delegated to ChatV2Provider which syncs tokens
 * automatically via useEffect when the session changes.
 */
export const ContextBridge: FC<ContextBridgeProps> = ({ children, defaultNamespace }) => {
  const [mountV2] = useChatV2();

  const bridgedMount = useCallback(
    (v1Args: ChatArgs = {}) => {
      // Let ChatV2Provider handle token syncing - don't pass tokens here
      // This avoids the type mismatch issue where V1 bridge was creating
      // objects with null values instead of returning null/undefined
      const v2Args = {
        namespace: defaultNamespace,
        topic: v1Args.topic,
        message: v1Args.askText,
        visibility: visibilityMap[v1Args.visibility ?? ''] ?? 'idle',
      };
      mountV2(v2Args);
    },
    [mountV2, defaultNamespace],
  );

  const bridgedValue = useMemo<ChatContextValue>(() => [bridgedMount], [bridgedMount]);

  return <ChatContext.Provider value={bridgedValue}>{children}</ChatContext.Provider>;
};
